import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { BlockMath } from "react-katex";
import MathLiveEditor from "../components/MathLiveEditor";
import { FigureRender } from "../components/FigureRender";
import SlatePreviewRender from "../components/SlatePreviewRender";
import PhotoNotebookViewport from "../components/PhotoNotebookViewport";
import { formatLatexForDisplay } from "../lib/latexUtils";

function getAttemptStatusLabel(status) {
  if (status === "submitted") return "Submitted";
  if (status === "correct") return "Correct";
  if (status === "incomplete") return "Incomplete";
  if (status === "needs_improvement") return "Needs Improvement";
  return "Needs Review";
}

function getAttemptStatusClass(status) {
  if (status === "submitted") return "status-correct";
  if (status === "correct") return "status-correct";
  if (status === "incomplete") return "status-incomplete";
  if (status === "needs_improvement") return "status-incorrect";
  return "status-missing";
}

function parseAttemptFeedback(rawFeedback) {
  const text = String(rawFeedback || "").trim();
  if (!text) return null;
  try {
    if (text.startsWith("```")) {
      const withoutFence = text.replace(/^```[a-zA-Z]*\n?/, "").replace(/```$/, "").trim();
      if (withoutFence.startsWith("{")) return JSON.parse(withoutFence);
    }
    if (text.startsWith("{")) return JSON.parse(text);
    const jsonStart = text.indexOf("{");
    const jsonEnd = text.lastIndexOf("}");
    if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
      return JSON.parse(text.slice(jsonStart, jsonEnd + 1));
    }
  } catch (err) {
    return null;
  }
  return null;
}

function buildFallbackAnalysis({ alignment = [], finalCorrect = null, rawFeedback = "" }) {
  const normalizedAlignment = Array.isArray(alignment) ? alignment : [];
  const correctCount = normalizedAlignment.filter((item) => item?.status === "correct").length;
  const firstIssue = normalizedAlignment.find((item) => item?.status && item.status !== "correct") || null;
  const whatWentRight =
    correctCount > 0
      ? `${correctCount} step${correctCount === 1 ? "" : "s"} aligned correctly with the saved analysis.`
      : finalCorrect
      ? "The final answer was marked correct."
      : "";
  const whereWrong =
    firstIssue && Number.isFinite(Number(firstIssue.student_step))
      ? `Step ${Number(firstIssue.student_step)}: ${String(firstIssue.reason || "This step needs review.")}`
      : "";
  const suggestion =
    firstIssue && !finalCorrect
      ? "Review the first flagged step and continue from the last confirmed correct relation."
      : "";
  if (whatWentRight || whereWrong || suggestion) {
    return {
      what_went_right: whatWentRight,
      where_it_went_wrong_or_unclear: whereWrong || null,
      next_step_suggestion: suggestion || null
    };
  }
  const fallbackText = String(rawFeedback || "").trim();
  if (!fallbackText) return null;
  return {
    what_went_right: "",
    where_it_went_wrong_or_unclear: fallbackText,
    next_step_suggestion: null
  };
}

function attachFigureStageMeta(figureSpec, sourceMeta) {
  if (!figureSpec || typeof figureSpec !== "object") return figureSpec;
  const width = Number(sourceMeta?.width || sourceMeta?.stageWidth || 0);
  const height = Number(sourceMeta?.height || sourceMeta?.stageHeight || 0);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return figureSpec;
  }
  return {
    ...figureSpec,
    canvas: {
      ...(figureSpec.canvas || {}),
      normalizedStageWidth: width,
      normalizedStageHeight: height
    }
  };
}

function AttemptReview({ apiBase, token, user, userRole, onPageBusyChange }) {
  const { attemptId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [frame, setFrame] = useState(null);
  const [question, setQuestion] = useState(null);
  const [studentInfo, setStudentInfo] = useState(null);
  const [isOwner, setIsOwner] = useState(true);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisAlignment, setAnalysisAlignment] = useState([]);
  const [analysisFeedback, setAnalysisFeedback] = useState("");
  const [selectedStep, setSelectedStep] = useState(null);
  const [figureTab, setFigureTab] = useState("drawing");
  const [solutionTab, setSolutionTab] = useState("slate");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reloadCounter, setReloadCounter] = useState(0);
  const [evaluatingErwin, setEvaluatingErwin] = useState(false);
  const [evalError, setEvalError] = useState("");

  useEffect(() => {
    if (typeof onPageBusyChange === "function") {
      onPageBusyChange("attempt-review", loading, "Loading attempt review...");
    }
    return () => {
      if (typeof onPageBusyChange === "function") {
        onPageBusyChange("attempt-review", false);
      }
    };
  }, [loading, onPageBusyChange]);

  useEffect(() => {
    let mounted = true;
    async function loadAttempt() {
      if (!token) return;
      setLoading(true);
      setError("");
      try {
        const response = await fetch(`${apiBase}/api/attempts/${attemptId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || "Failed to load attempt");
        }
        const payload = await response.json();
        if (!mounted) return;
        setAttempt(payload.attempt || null);
        setFrame(payload.frame || null);
        setQuestion(payload.question || null);
        setStudentInfo(payload.student || null);
        setIsOwner(typeof payload.isOwner === "boolean" ? payload.isOwner : (user?.uid ? String(payload?.attempt?.userId) === String(user.uid) : true));

        const hasPhotos = Array.isArray(payload?.attempt?.uploadedSolutionImages) && payload.attempt.uploadedSolutionImages.length > 0;
        if (payload?.attempt?.submissionType === "photo_upload" || hasPhotos) {
          setSolutionTab("photos");
        }

        const rawFeedback = payload?.attempt?.aiFeedback || "";
        const parsed = parseAttemptFeedback(rawFeedback);
        const alignment = Array.isArray(parsed?.alignment)
          ? parsed.alignment
          : Array.isArray(payload?.attempt?.aiAlignment)
          ? payload.attempt.aiAlignment
          : [];
        const fallbackAnalysis = buildFallbackAnalysis({
          alignment,
          finalCorrect:
            typeof parsed?.final_answer_correct === "boolean"
              ? parsed.final_answer_correct
              : payload?.attempt?.aiFinalCorrect,
          rawFeedback
        });

        if (parsed?.analysis || fallbackAnalysis) {
          setAnalysisData(parsed?.analysis || fallbackAnalysis);
          setAnalysisAlignment(alignment);
          const firstIssue = alignment.find(
            (item) => item?.status && item.status !== "correct" && Number.isFinite(Number(item.student_step))
          );
          setSelectedStep(firstIssue ? Number(firstIssue.student_step) : null);
          setAnalysisFeedback(parsed?.analysis ? "" : rawFeedback || "No feedback");
        } else {
          setAnalysisData(null);
          setAnalysisAlignment([]);
          setSelectedStep(null);
          setAnalysisFeedback(rawFeedback || "No feedback");
        }
      } catch (err) {
        if (mounted) setError(err.message || "Failed to load attempt");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadAttempt();
    return () => {
      mounted = false;
    };
  }, [apiBase, token, attemptId, reloadCounter]);

  useEffect(() => {
    setFigureTab("drawing");
  }, [attemptId]);

  const hasWorkToEvaluate = useMemo(() => {
    if (!attempt) return false;
    const hasLines = (Array.isArray(attempt.slateLines) && attempt.slateLines.length > 0) ||
      (attempt.slateMeta && Array.isArray(attempt.slateMeta.lines) && attempt.slateMeta.lines.length > 0);
    const hasSteps = (Array.isArray(attempt.ocrEditedSteps) && attempt.ocrEditedSteps.length > 0) ||
      Boolean(String(attempt.ocrEditedLatex || attempt.ocrOriginalLatex || "").trim());
    const hasPhotos = Array.isArray(attempt.uploadedSolutionImages) && attempt.uploadedSolutionImages.length > 0;
    return hasLines || hasSteps || hasPhotos;
  }, [attempt]);

  const handleEvaluateWithErwin = async () => {
    if (!attemptId || evaluatingErwin) return;
    setEvaluatingErwin(true);
    setEvalError("");
    try {
      const res = await fetch(`${apiBase}/api/attempts/${attemptId}/analyze-equivalence`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || body.details?.message || "Erwin AI evaluation failed");
      }
      setReloadCounter((c) => c + 1);
    } catch (err) {
      console.error("[AttemptReview] Erwin evaluation error:", err);
      setEvalError(err.message || "Failed to evaluate steps with Erwin AI");
    } finally {
      setEvaluatingErwin(false);
    }
  };

  const stepDiagnostics = useMemo(() => {
    const map = {};
    analysisAlignment.forEach((item) => {
      const stepNo = Number(item?.student_step);
      if (!Number.isFinite(stepNo) || stepNo <= 0) return;
      map[stepNo] = {
        status: item.status || "review",
        reason: item.reason || ""
      };
    });
    return map;
  }, [analysisAlignment]);

  const studentLatex =
    attempt?.ocrEditedLatex ||
    attempt?.ocrOriginalLatex ||
    (attempt?.ocrEditedSteps || attempt?.ocrOriginalSteps || []).join(" \\\\ ");
  const recoveredSlateSteps = (
    (Array.isArray(attempt?.ocrEditedSteps) && attempt.ocrEditedSteps.length
      ? attempt.ocrEditedSteps
      : Array.isArray(attempt?.ocrOriginalSteps) && attempt.ocrOriginalSteps.length
      ? attempt.ocrOriginalSteps
      : Array.isArray(attempt?.ocrLiveLines)
      ? attempt.ocrLiveLines.map((line) => String(line?.lineLatex || "").trim()).filter(Boolean)
      : []) || []
  ).filter(Boolean);

  if (loading) {
    return (
      <section className="page">
        <div className="question-meta">Loading attempt...</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="page">
        <div className="error-text">{error}</div>
      </section>
    );
  }

  if (!attempt) {
    return (
      <section className="page">
        <div className="question-meta">Attempt not found.</div>
      </section>
    );
  }

  const questionLatex = question?.latex || frame?.latexTemplate || "";
  const questionFigure =
    question?.figureRenderSpec || question?.figureSpec || frame?.figureSpec || null;
  const questionPresentation =
    (question?.frameSchema && typeof question.frameSchema === "object" ? question.frameSchema.presentation : null) ||
    (frame?.frameSchema && typeof frame.frameSchema === "object" ? frame.frameSchema.presentation : null) ||
    {};
  const studentFigureBase =
    attempt?.studentFigureRenderSpec || attempt?.studentFigureSpec || null;
  const studentFigure = attachFigureStageMeta(studentFigureBase, attempt?.studentFigureSourceMeta || null);
  const studentFigureImageDataUrl =
    typeof attempt?.studentFigureImageDataUrl === "string" &&
    String(attempt.studentFigureImageDataUrl).includes(",")
      ? String(attempt.studentFigureImageDataUrl)
      : null;
  const studentFigureSummary = attempt?.studentFigureSummary || null;
  const figureEntitySummary = Array.isArray(studentFigureSummary?.entitySummary)
    ? studentFigureSummary.entitySummary
    : [];
  const figureRelationSummary = Array.isArray(studentFigureSummary?.relationSummary)
    ? studentFigureSummary.relationSummary
    : [];
  const figureDependencySummary = Array.isArray(studentFigureSummary?.dependencySummary)
    ? studentFigureSummary.dependencySummary
    : [];
  const figureGivenSummary = Array.isArray(studentFigureSummary?.givenSummary)
    ? studentFigureSummary.givenSummary
    : [];
  const figureWarnings = Array.isArray(studentFigureSummary?.warnings) ? studentFigureSummary.warnings : [];
  const showQuestionFigure =
    Boolean(questionFigure) &&
    (typeof questionPresentation.showQuestionFigure === "boolean"
      ? questionPresentation.showQuestionFigure
      : true);
  const reviewFigure = studentFigure || (showQuestionFigure ? questionFigure : null);
  const reviewFigureTheme = studentFigure ? "accepted" : "default";

  const retryParams = new URLSearchParams();
  if (attempt?.difficulty) retryParams.set("difficulty", attempt.difficulty);
  if (Number.isFinite(Number(attempt?.frameOrder))) {
    retryParams.set("frameOrder", String(Number(attempt.frameOrder)));
  }
  if (attempt?._id) retryParams.set("retryAttemptId", String(attempt._id));
  const isCustomAttempt = !attempt?.frameId;
  const customQuestionId = question?.id || (attempt?.questionId?._id ? String(attempt.questionId._id) : (attempt?.questionId ? String(attempt.questionId) : ""));
  const retryHref = isCustomAttempt
    ? `/practice/custom/${customQuestionId}`
    : `/practice/${attempt.chapterId}${retryParams.toString() ? `?${retryParams.toString()}` : ""}`;
  const isViewerTutorOrAdmin = userRole === "tutor" || userRole === "admin" || !isOwner;
  const defaultBackHref = isCustomAttempt
    ? (attempt?.assignmentSetId
      ? (isViewerTutorOrAdmin ? `/tutor/assignments/${attempt.assignmentSetId}` : "/my-questions?tab=homework")
      : (customQuestionId ? `/my-questions/${customQuestionId}/attempts` : "/my-questions"))
    : (attempt?.chapterId && (frame?.id || attempt?.frameId)
      ? `/chapters/${attempt.chapterId}/frames/${frame?.id || attempt?.frameId}`
      : attempt?.chapterId
      ? `/chapters/${attempt.chapterId}`
      : "/");
  const backHref = location?.state?.backTo || defaultBackHref;
  const attemptStatus = String(attempt?.statusBadge || "").trim() || "needs_review";
  const handleBack = () => {
    if (location?.state?.backTo) {
      navigate(location.state.backTo);
    } else if (backHref) {
      navigate(backHref);
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(defaultBackHref);
    }
  };

  return (
    <section className="page">
      <div className="practice-header attempt-review-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
        <div className="practice-left" style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
          <button
            type="button"
            className="back-link"
            onClick={handleBack}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              font: "inherit",
              color: "var(--ink)",
              display: "flex",
              alignItems: "center",
              gap: "4px"
            }}
          >
            {"\u2190 Back"}
          </button>
          {!isViewerTutorOrAdmin && !attempt?.isTestMode && (
            <Link
              to={retryHref}
              className="primary-button"
              style={{ width: "auto", padding: "4px 12px", fontSize: "12px", textAlign: "center", whiteSpace: "nowrap" }}
            >
              Retry
            </Link>
          )}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div className="page-title" style={{ margin: 0 }}>{frame?.title || question?.title || "Attempt Review"}</div>
              {attempt?.isTestMode && (
                <span style={{ backgroundColor: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5", fontSize: "11px", fontWeight: "700", padding: "2px 8px", borderRadius: "6px" }}>
                  ⏱️ EXAM
                </span>
              )}
            </div>
            <div className="page-sub">
              {isViewerTutorOrAdmin
                ? `Reviewing student submission by ${studentInfo?.name || studentInfo?.email || "Student"}`
                : "Review your work and tutor feedback."}
            </div>
          </div>
        </div>
      </div>

      <div className="question-block">
        <div className="review-question-title-row">
          <div className="review-question-title-left">
            <div className="question-meta">Level: {attempt.difficulty}</div>
            {frame?.title && <div className="question-title">{frame.title}</div>}
          </div>
          <span className={`attempt-badge ${getAttemptStatusClass(attemptStatus)}`}>
            {getAttemptStatusLabel(attemptStatus)}
          </span>
        </div>
        {questionLatex ? (
          <div className="practice-question-wrap">
            <BlockMath math={formatLatexForDisplay(questionLatex)} />
          </div>
        ) : (
          <div>No question loaded</div>
        )}
        {(reviewFigure ||
          figureEntitySummary.length > 0 ||
          figureRelationSummary.length > 0 ||
          figureDependencySummary.length > 0 ||
          figureGivenSummary.length > 0 ||
          figureWarnings.length > 0) && (
          <>
            <div className="work-tabs">
              <button
                type="button"
                className={`tool-chip ${figureTab === "drawing" ? "active" : ""}`}
                onClick={() => setFigureTab("drawing")}
              >
                Drawing Slate
              </button>
              <button
                type="button"
                className={`tool-chip ${figureTab === "editor" ? "active" : ""}`}
                onClick={() => setFigureTab("editor")}
              >
                Editor
              </button>
            </div>
            {figureTab === "drawing" ? (
              <div className="figure-assist-preview-card">
                <div className="question-meta">
                  {studentFigureImageDataUrl || studentFigure ? "Captured Figure" : "Question Figure"}
                </div>
                {studentFigureImageDataUrl ? (
                  <div className="figure-block">
                    <img
                      className="attempt-figure-image"
                      src={studentFigureImageDataUrl}
                      alt="Captured figure"
                    />
                  </div>
                ) : reviewFigure ? (
                  <div className="figure-block">
                    <FigureRender
                      figureSpec={reviewFigure}
                      theme={reviewFigureTheme}
                      cropToContent
                    />
                  </div>
                ) : (
                  <div className="question-meta">No figure was submitted.</div>
                )}
              </div>
            ) : (
              <div className="figure-assist-summary-card attempt-review-editor">
                {figureEntitySummary.length > 0 && (
                  <>
                    <div className="analysis-label">Entities</div>
                    <div className="figure-summary-list">
                      {figureEntitySummary.map((item, index) => (
                        <div key={`review-entity-${index}`} className="analysis-text">
                          {item}
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {figureRelationSummary.length > 0 && (
                  <>
                    <div className="analysis-label">Relations</div>
                    <div className="figure-summary-list">
                      {figureRelationSummary.map((item, index) => (
                        <div key={`review-relation-${index}`} className="analysis-text">
                          {item}
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {figureDependencySummary.length > 0 && (
                  <>
                    <div className="analysis-label">Dependencies</div>
                    <div className="figure-summary-list">
                      {figureDependencySummary.map((item, index) => (
                        <div key={`review-dependency-${index}`} className="analysis-text">
                          {item}
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {figureGivenSummary.length > 0 && (
                  <>
                    <div className="analysis-label">Givens</div>
                    <div className="figure-summary-list">
                      {figureGivenSummary.map((item, index) => (
                        <div key={`review-given-${index}`} className="analysis-text">
                          {item}
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {figureWarnings.length > 0 && (
                  <>
                    <div className="analysis-label">Warnings</div>
                    <div className="figure-summary-list">
                      {figureWarnings.map((item, index) => (
                        <div key={`review-warning-${index}`} className="analysis-text">
                          {item}
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {!figureEntitySummary.length &&
                  !figureRelationSummary.length &&
                  !figureDependencySummary.length &&
                  !figureGivenSummary.length &&
                  !figureWarnings.length && (
                    <div className="question-meta">No captured dependencies or givens.</div>
                  )}
              </div>
            )}
          </>
        )}
      </div>

      <div className="review-card">
        <div className="work-tabs">
          {Array.isArray(attempt?.uploadedSolutionImages) && attempt.uploadedSolutionImages.length > 0 && (
            <button
              type="button"
              className={`tool-chip ${solutionTab === "photos" ? "active" : ""}`}
              onClick={() => setSolutionTab("photos")}
            >
              Notebook Photos ({attempt.uploadedSolutionImages.length})
            </button>
          )}
          <button
            type="button"
            className={`tool-chip ${solutionTab === "slate" ? "active" : ""}`}
            onClick={() => setSolutionTab("slate")}
          >
            Slate
          </button>
          <button
            type="button"
            className={`tool-chip ${solutionTab === "editor" ? "active" : ""}`}
            onClick={() => setSolutionTab("editor")}
          >
            Math Editor
          </button>
        </div>
        {solutionTab === "photos" ? (
          <div className="attempt-uploaded-photos-viewer">
            <PhotoNotebookViewport
              images={attempt.uploadedSolutionImages || []}
              detectedStepBoxes={attempt.detectedStepBoxes || []}
              stepDiagnostics={stepDiagnostics}
              selectedStep={selectedStep}
              onStepSelect={setSelectedStep}
              showBoxes={true}
            />
          </div>
        ) : solutionTab === "slate" ? (
          <>
            <div className="question-meta">
              {isViewerTutorOrAdmin
                ? `${studentInfo?.name ? `${studentInfo.name}'s Handwritten Slate` : "Student Handwritten Slate"}`
                : "Your Slate"}
            </div>
            {(() => {
              const displayLines = (Array.isArray(attempt?.slateLines) && attempt.slateLines.length > 0)
                ? attempt.slateLines
                : (Array.isArray(attempt?.slateMeta?.lines) ? attempt.slateMeta.lines : []);
              if (displayLines.length > 0) {
                return (
                  <SlatePreviewRender
                    lines={displayLines}
                    stageWidth={attempt?.slateMeta?.stageWidth}
                    stageHeight={attempt?.slateMeta?.stageHeight}
                    laneHeights={attempt?.slateMeta?.laneHeights}
                    stepDiagnostics={stepDiagnostics}
                    selectedStep={selectedStep}
                    onSelectStep={setSelectedStep}
                  />
                );
              }
              if (attempt?.slatePreviewUrl || attempt?.slateUrl) {
                return (
                  <img
                    className="slate-preview"
                    src={attempt.slatePreviewUrl || attempt.slateUrl}
                    alt="Slate attempt"
                  />
                );
              }
              if (recoveredSlateSteps.length > 0) {
                return (
                  <div className="attempt-recovered-slate">
                    <div className="question-meta">
                      Handwritten slate was not saved for this older attempt. Showing recovered OCR steps instead.
                    </div>
                    <div className="attempt-recovered-slate-list">
                      {recoveredSlateSteps.map((step, index) => (
                        <div key={`recovered-slate-step-${index}`} className="attempt-recovered-slate-step">
                          <BlockMath math={step} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              if (attempt?.isUnanswered) {
                return (
                  <div style={{ padding: "32px 20px", textAlign: "center", backgroundColor: "#f8fafc", borderRadius: "10px", border: "1.5px dashed #cbd5e1" }}>
                    <div style={{ fontSize: "28px", marginBottom: "8px" }}>📝</div>
                    <div style={{ fontWeight: "700", color: "#475569", fontSize: "15px" }}>Question Left Unanswered</div>
                    <div style={{ fontSize: "13px", color: "#94a3b8", marginTop: "4px" }}>
                      No handwritten slate or photo solution was submitted for this question during the exam.
                    </div>
                  </div>
                );
              }
              return <div className="question-meta">No slate image saved.</div>;
            })()}
          </>
        ) : (
          <>
            <div className="question-meta">Math Editor (read-only)</div>
            <MathLiveEditor
              value={studentLatex || ""}
              onChange={() => {}}
              readOnly
              showKeyboard={false}
              stepDiagnostics={stepDiagnostics}
              selectedStep={selectedStep}
              onStepSelect={setSelectedStep}
            />
          </>
        )}
      </div>

      {/* ── EXAM PENDING PUBLICATION BANNER ── */}
      {attempt?.isTestMode && !attempt?.gradesPublished && !isViewerTutorOrAdmin && (
        <div
          style={{
            backgroundColor: "#fffbeb",
            border: "1.5px solid #fde68a",
            borderRadius: "12px",
            padding: "16px 20px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: "12px"
          }}
        >
          <span style={{ fontSize: "24px" }}>⏳</span>
          <div>
            <div style={{ fontWeight: "700", color: "#92400e", fontSize: "14px" }}>Grading In Progress</div>
            <div style={{ fontSize: "13px", color: "#b45309", marginTop: "2px" }}>
              {attempt?.testLockedMessage || "Test submitted. Scores and rubric breakdown will be available once your teacher finalizes and publishes grading."}
            </div>
          </div>
        </div>
      )}

      {/* ── EXAM SCORE CARD ── */}
      {attempt?.isTestMode && (attempt?.gradesPublished || isViewerTutorOrAdmin) && attempt?.marksMax > 0 && (
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1.5px solid #cbd5e1",
            borderRadius: "12px",
            padding: "16px 22px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "28px" }}>🏆</span>
            <div>
              <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", fontWeight: "700" }}>
                Curriculum Exam Result
              </div>
              <div style={{ fontSize: "22px", fontWeight: "700", color: "#0f172a" }}>
                {attempt.marksAwarded !== null && attempt.marksAwarded !== undefined ? attempt.marksAwarded : "—"} / {attempt.marksMax} Marks
              </div>
            </div>
          </div>

          {attempt.marksAwarded !== null && attempt.marksMax > 0 && (
            <div
              style={{
                fontSize: "15px",
                fontWeight: "700",
                padding: "4px 14px",
                borderRadius: "8px",
                backgroundColor: attempt.isUnanswered
                  ? "#f1f5f9"
                  : (attempt.marksAwarded / attempt.marksMax) >= 0.8
                  ? "#ecfdf5"
                  : (attempt.marksAwarded / attempt.marksMax) >= 0.5
                  ? "#eff6ff"
                  : "#fef2f2",
                color: attempt.isUnanswered
                  ? "#64748b"
                  : (attempt.marksAwarded / attempt.marksMax) >= 0.8
                  ? "#059669"
                  : (attempt.marksAwarded / attempt.marksMax) >= 0.5
                  ? "#2563eb"
                  : "#dc2626",
                border: "1px solid currentColor"
              }}
            >
              {attempt.isUnanswered ? "Unanswered (0%)" : `${Math.round((attempt.marksAwarded / attempt.marksMax) * 100)}%`}
            </div>
          )}
        </div>
      )}

      {/* ── 100-POINT RUBRIC EVALUATION BREAKDOWN ── */}
      {attempt?.isTestMode && !attempt?.isUnanswered && (attempt?.gradesPublished || isViewerTutorOrAdmin) && Array.isArray(attempt?.rubricEvaluation) && attempt.rubricEvaluation.length > 0 && (
        <div
          className="analysis-box"
          style={{
            backgroundColor: "#ffffff",
            border: "1.5px solid #e2e8f0",
            borderRadius: "12px",
            padding: "20px 24px",
            marginBottom: "20px"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "18px" }}>🎯</span>
              <div style={{ fontWeight: "700", color: "#0f172a", fontSize: "16px" }}>
                Curriculum Rubric Breakdown (100-Point Fixed Weights)
              </div>
            </div>
            <div style={{ fontSize: "12px", color: "var(--muted)" }}>
              Scaled to {attempt.marksMax} Question Marks
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {attempt.rubricEvaluation.map((crit, idx) => (
              <div
                key={idx}
                style={{
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "12px 16px"
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                  <div style={{ fontWeight: "600", fontSize: "14px", color: "#1e293b" }}>
                    {crit.criterion}
                  </div>
                  <div
                    style={{
                      fontWeight: "700",
                      fontSize: "13px",
                      color: crit.pointsEarned === crit.maxPoints ? "#059669" : crit.pointsEarned > 0 ? "#d97706" : "#dc2626",
                      backgroundColor: "#ffffff",
                      padding: "2px 8px",
                      borderRadius: "6px",
                      border: "1px solid #e2e8f0"
                    }}
                  >
                    {crit.pointsEarned} / {crit.maxPoints} pts
                  </div>
                </div>

                {crit.feedback && (
                  <div style={{ fontSize: "13px", color: "#475569", lineHeight: "1.4" }}>
                    {crit.feedback}
                  </div>
                )}

                {Array.isArray(crit.matchedSteps) && crit.matchedSteps.length > 0 && (
                  <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "4px" }}>
                    Matched Steps: {crit.matchedSteps.join(", ")}
                  </div>
                )}
              </div>
            ))}
          </div>

          {attempt.aiCohortFeedback && (
            <div style={{ marginTop: "14px", padding: "10px 14px", backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", fontSize: "13px", color: "#14532d" }}>
              <strong>AI Cohort Evaluator:</strong> {attempt.aiCohortFeedback}
            </div>
          )}
        </div>
      )}

      {/* ── STUDENT PERFORMANCE & MASTERY DIAGNOSTICS CARD ── */}
      {attempt?.performanceDiagnostics && (attempt.gradesPublished || !attempt.isTestMode || isViewerTutorOrAdmin) && (
        <div
          className="analysis-box"
          style={{
            backgroundColor: "#ffffff",
            border: "1.5px solid #cbd5e1",
            borderRadius: "12px",
            padding: "20px 24px",
            marginBottom: "20px"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px", marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "20px" }}>📊</span>
              <div>
                <div style={{ fontWeight: "700", color: "#0f172a", fontSize: "16px" }}>
                  Performance & Mastery Diagnostics
                </div>
                <div style={{ fontSize: "12px", color: "var(--muted)" }}>
                  Measured across concept understanding, algebraic accuracy, transformations, and step reduction.
                </div>
              </div>
            </div>

            <div
              style={{
                fontSize: "12px",
                fontWeight: "700",
                padding: "3px 10px",
                borderRadius: "12px",
                backgroundColor: attempt.performanceDiagnostics.statusBand === "strong" ? "#ecfdf5" : attempt.performanceDiagnostics.statusBand === "good" ? "#eff6ff" : attempt.performanceDiagnostics.statusBand === "needs_improvement" ? "#fffbeb" : "#fef2f2",
                color: attempt.performanceDiagnostics.statusBand === "strong" ? "#059669" : attempt.performanceDiagnostics.statusBand === "good" ? "#2563eb" : attempt.performanceDiagnostics.statusBand === "needs_improvement" ? "#d97706" : "#dc2626",
                border: "1px solid currentColor"
              }}
            >
              {attempt.performanceDiagnostics.statusBand === "strong" ? "🟢 Mastered / Strong" : attempt.performanceDiagnostics.statusBand === "good" ? "🔵 Good / Developing" : attempt.performanceDiagnostics.statusBand === "needs_improvement" ? "🟡 Needs Improvement" : "🔴 Focus Needed / Weak"} ({attempt.performanceDiagnostics.percentage}%)
            </div>
          </div>

          {/* 4 Parameter Progress Gauges */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "10px", marginBottom: "16px" }}>
            <div style={{ padding: "10px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", fontWeight: "700" }}>Concept Mastery</div>
              <div style={{ fontSize: "18px", fontWeight: "700", color: "#0f172a", marginTop: "2px" }}>
                {attempt.performanceDiagnostics.conceptScore}%
              </div>
              <div style={{ height: "4px", backgroundColor: "#e2e8f0", borderRadius: "2px", marginTop: "4px", overflow: "hidden" }}>
                <div style={{ width: `${attempt.performanceDiagnostics.conceptScore}%`, height: "100%", backgroundColor: "#3b82f6" }} />
              </div>
            </div>

            <div style={{ padding: "10px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", fontWeight: "700" }}>Algebraic Accuracy</div>
              <div style={{ fontSize: "18px", fontWeight: "700", color: attempt.performanceDiagnostics.algebraicScore >= 60 ? "#059669" : "#d97706", marginTop: "2px" }}>
                {attempt.performanceDiagnostics.algebraicScore}%
              </div>
              <div style={{ height: "4px", backgroundColor: "#e2e8f0", borderRadius: "2px", marginTop: "4px", overflow: "hidden" }}>
                <div style={{ width: `${attempt.performanceDiagnostics.algebraicScore}%`, height: "100%", backgroundColor: attempt.performanceDiagnostics.algebraicScore >= 60 ? "#10b981" : "#f59e0b" }} />
              </div>
            </div>

            <div style={{ padding: "10px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", fontWeight: "700" }}>Transformation Fluency</div>
              <div style={{ fontSize: "18px", fontWeight: "700", color: "#6366f1", marginTop: "2px" }}>
                {attempt.performanceDiagnostics.transformationScore}%
              </div>
              <div style={{ height: "4px", backgroundColor: "#e2e8f0", borderRadius: "2px", marginTop: "4px", overflow: "hidden" }}>
                <div style={{ width: `${attempt.performanceDiagnostics.transformationScore}%`, height: "100%", backgroundColor: "#6366f1" }} />
              </div>
            </div>

            <div style={{ padding: "10px 14px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: "11px", color: "var(--muted)", textTransform: "uppercase", fontWeight: "700" }}>Derivation Steps & Depth</div>
              <div style={{ fontSize: "18px", fontWeight: "700", color: "#0f172a", marginTop: "2px" }}>
                {attempt.performanceDiagnostics.stepCount} Steps
              </div>
              <div style={{ fontSize: "11px", color: attempt.performanceDiagnostics.isComplete === false ? "#d97706" : "#059669", fontWeight: "600", marginTop: "2px" }}>
                {attempt.performanceDiagnostics.isComplete === false
                  ? `Incomplete derivation (${attempt.performanceDiagnostics.reachedMilestonesCount || 1} of ${attempt.performanceDiagnostics.canonicalStepCount || 3} milestones reached)`
                  : `${(attempt.performanceDiagnostics.stepEfficiencyRatio || 1) <= 1.5 ? "✓ Direct proof" : (attempt.performanceDiagnostics.stepEfficiencyRatio || 1) <= 3.8 ? "✓ Thorough derivation" : "Extended steps"} • ${attempt.performanceDiagnostics.canonicalStepCount ? `${attempt.performanceDiagnostics.canonicalStepCount} key milestones` : 'Standard solution'}`}
              </div>
            </div>
          </div>

          {/* Three Pedagogical Columns: Strengths, Errors, Focus Points */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px" }}>
            {/* Strengths */}
            <div style={{ padding: "12px 14px", backgroundColor: "#ecfdf5", borderRadius: "8px", border: "1px solid #a7f3d0" }}>
              <div style={{ fontSize: "12px", fontWeight: "700", color: "#065f46", marginBottom: "6px" }}>
                🌟 Strengths Demonstrated
              </div>
              {Array.isArray(attempt.performanceDiagnostics.strengths) && attempt.performanceDiagnostics.strengths.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: "16px", fontSize: "12px", color: "#047857" }}>
                  {attempt.performanceDiagnostics.strengths.map((s, idx) => (
                    <li key={idx} style={{ marginBottom: "3px" }}>{s}</li>
                  ))}
                </ul>
              ) : (
                <div style={{ fontSize: "12px", color: "#065f46", fontStyle: "italic" }}>Sound solution initiation.</div>
              )}
            </div>

            {/* Algebraic Errors */}
            <div style={{ padding: "12px 14px", backgroundColor: "#fff7ed", borderRadius: "8px", border: "1px solid #fed7aa" }}>
              <div style={{ fontSize: "12px", fontWeight: "700", color: "#9a3412", marginBottom: "6px" }}>
                ⚠️ Algebraic Slips & Jumps
              </div>
              {Array.isArray(attempt.performanceDiagnostics.errors) && attempt.performanceDiagnostics.errors.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: "16px", fontSize: "12px", color: "#c2410c" }}>
                  {attempt.performanceDiagnostics.errors.map((e, idx) => (
                    <li key={idx} style={{ marginBottom: "3px" }}>{e}</li>
                  ))}
                </ul>
              ) : (
                <div style={{ fontSize: "12px", color: "#9a3412", fontStyle: "italic" }}>No major calculation slips.</div>
              )}
            </div>

            {/* Focus Points */}
            <div style={{ padding: "12px 14px", backgroundColor: "#f0fdf4", borderRadius: "8px", border: "1px solid #bbf7d0" }}>
              <div style={{ fontSize: "12px", fontWeight: "700", color: "#166534", marginBottom: "6px" }}>
                🎯 Recommended Practice Focus
              </div>
              {Array.isArray(attempt.performanceDiagnostics.focusPoints) && attempt.performanceDiagnostics.focusPoints.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: "16px", fontSize: "12px", color: "#15803d" }}>
                  {attempt.performanceDiagnostics.focusPoints.map((fp, idx) => (
                    <li key={idx} style={{ marginBottom: "3px" }}>{fp}</li>
                  ))}
                </ul>
              ) : (
                <div style={{ fontSize: "12px", color: "#166534", fontStyle: "italic" }}>Continue regular practice.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── TEACHER'S PERSONAL REMARK CARD ── */}
      {attempt?.tutorFeedback?.notes && (
        <div
          className="analysis-box"
          style={{
            backgroundColor: "#f5f3ff",
            border: "1.5px solid #c4b5fd",
            borderRadius: "12px",
            padding: "20px 24px",
            marginBottom: "20px"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
            <span style={{ fontSize: "16px" }}>🧑‍🏫</span>
            <div style={{ fontWeight: "700", color: "#5b21b6", fontSize: "15px" }}>Teacher's Personal Remark</div>
            {attempt.tutorFeedback.gradedAt && (
              <span style={{ fontSize: "12px", color: "#7c3aed", marginLeft: "auto" }}>
                {new Date(attempt.tutorFeedback.gradedAt).toLocaleDateString("en-IN", { month: "short", day: "numeric" })}
              </span>
            )}
          </div>
          <div style={{ color: "#3730a3", fontSize: "14px", lineHeight: "1.6" }}>
            {attempt.tutorFeedback.notes}
          </div>
        </div>
      )}

      {!attempt?.isTestMode && (
        <div className="analysis-box">
          <div className="question-meta">Tutor Feedback (Erwin AI)</div>
          {analysisData ? (
            <div className="analysis-grid">
              {analysisData.what_went_right && (
                <div>
                  <div className="analysis-label">What went right</div>
                  <div className="analysis-text">{analysisData.what_went_right}</div>
                </div>
              )}
              {analysisData.where_it_went_wrong_or_unclear && (
                <div>
                  <div className="analysis-label">Where it went wrong or unclear</div>
                  <div className="analysis-text">{analysisData.where_it_went_wrong_or_unclear}</div>
                </div>
              )}
              {analysisData.next_step_suggestion && (
                <div>
                  <div className="analysis-label">Next step suggestion</div>
                  <div className="analysis-text">{analysisData.next_step_suggestion}</div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="analysis-text">{analysisFeedback || "No feedback yet."}</div>
              {(isViewerTutorOrAdmin || isOwner) && hasWorkToEvaluate && (
                <div style={{ marginTop: "14px", padding: "14px 18px", backgroundColor: "#f0fdf4", border: "1px solid #86efac", borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: "700", color: "#166534", fontSize: "14px" }}>
                      Handwritten Solution Awaiting Evaluation
                    </div>
                    <div style={{ fontSize: "12px", color: "#15803d", marginTop: "2px" }}>
                      Run Erwin AI to evaluate mathematical step equivalence, algebraic accuracy, and mastery diagnostics.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="primary-button"
                    disabled={evaluatingErwin}
                    onClick={handleEvaluateWithErwin}
                    style={{ padding: "8px 16px", fontSize: "13px", fontWeight: "600", whiteSpace: "nowrap", backgroundColor: "#059669", color: "#ffffff" }}
                  >
                    {evaluatingErwin ? "Evaluating with Erwin..." : "⚡ Evaluate with Erwin AI"}
                  </button>
                </div>
              )}
              {evalError && (
                <div style={{ marginTop: "10px", padding: "10px 14px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", color: "#b91c1c", fontSize: "13px" }}>
                  ⚠️ {evalError}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default AttemptReview;
